// src/components/admin/ArchivedSubmissions.jsx
import React, { useState, useEffect } from 'react';
import { 
  Users, 
  FileText, 
  CheckCircle, 
  Upload,
  Archive,
  UserPlus,
  Trash2,
  Search,
  ChevronLeft,
  ChevronRight,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';
import SubmissionDetailsModal from './modals/SubmissionDetailsModal';
import { showToast } from '../../utils/toast';
import { getStatusClass } from '../../utils/utils.global';

const ArchivedSubmissions = () => {
  const {user} = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [showActionsDropdown, setShowActionsDropdown] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setshowDeleteModal] = useState(false);
  const [archiveSubmissionId, setArchiveSubmissionId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recentSubmissions, setRecentSubmissions] = useState([]);

  // Mock stats data
  const stats = {
    submissions: 199,
    authors: 341,
    reviewed: 0,
    decisioned: 156,
    archived: 89,
    editorInvitations: 11
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch submissions with debounced search
  useEffect(() => {
    const fetchSubmissions = async () => {
      setLoading(true);
      try {
        const endpoint =  `/editors/archivedSubmissions`;
        const response = await axios.get(endpoint, {
          params: {
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearchTerm
          }
        });

        if (response.data.success) {
          setRecentSubmissions(response.data.submissions);
          setTotalItems(response.data.total);
        } else if (response.data.submissions) {
          // Handle older format if needed
          setRecentSubmissions(response.data.submissions);
          setTotalItems(response.data.total || response.data.submissions.length);
        }
      } catch (error) {
        console.error('Error fetching submissions:', error);
        showToast('Failed to load submissions', 'error');
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if user is loaded
    if (user) {
      fetchSubmissions();
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm, user]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleViewSubmission = (submission) => {
    setSelectedSubmission(submission);
    setShowViewModal(true);
    setShowActionsDropdown(null);
  };

  const handleDeleteClick = (id) => {
    setArchiveSubmissionId(id);
    setshowDeleteModal(true);
    setShowActionsDropdown(null);
  };

  const handleDeleteConfirm = () => {
    setDeleteLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      // In real app, this would make an API call to archive the submission
      console.log('Delete submission:', archiveSubmissionId);
      
      setDeleteLoading(false);
      setshowDeleteModal(false);
      setArchiveSubmissionId(null);
      
      // Show success message
      showToast('Submission archived successfully', 'success');
    }, 1500);
  };

  const handleDeleteCancel = () => {
    setshowDeleteModal(false);
    setArchiveSubmissionId(null);
  };


  // Filter submissions based on search
  const filteredSubmissions = recentSubmissions.filter(sub => 
    sub.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sub.id?.toLowerCase().includes(searchTerm.toLowerCase()) 
    // (sub.authors && sub.authors.some(a => a?.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  // Pagination
  const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSubmissions = filteredSubmissions.slice(startIndex, endIndex);

  const statsCards = [
    { 
      label: 'Submissions', 
      value: stats.submissions, 
      icon: Upload, 
      color: 'bg-blue-500',
      link: '/admin/dashboard'
    },
    { 
      label: 'Authors', 
      value: stats.authors, 
      icon: Users, 
      color: 'bg-green-500',
      link: '/editors/admin/authors'
    },
    { 
      label: 'Reviewed', 
      value: stats.reviewed, 
      icon: CheckCircle, 
      color: 'bg-purple-500',
      link: '/editors/reviews'
    },
    { 
      label: 'Decisioned', 
      value: stats.decisioned, 
      icon: FileText, 
      color: 'bg-orange-500',
      link: '/editors/decisioned'
    },
    { 
      label: 'Archived', 
      value: stats.archived, 
      icon: Archive, 
      color: 'bg-gray-500',
      link: '/editors/admin/archived'
    },
    { 
      label: 'Editor invitations', 
      value: stats.editorInvitations, 
      icon: UserPlus, 
      color: 'bg-red-500',
      link: '/editors/editor-invitations'
    }
  ];

  if (loading && recentSubmissions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-purple-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading archived submissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4 py-4">
      {/* Archived Submissions Table */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">Archived Submissions</h3>
          <div className="mt-3 md:mt-0 md:w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search submissions..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent text-sm"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submission
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reviewer Invitations
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Editor Invitations
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentSubmissions.map((submission) => (
                <tr key={submission.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="max-w-md">
                      <p className="text-sm font-medium text-gray-900">{submission.title}</p>
                      <p className="text-xs text-[--asfi-purple] mt-1">{submission.id}</p>
                      {submission.isWomenInScience && (
                        <span className="inline-flex items-center px-2 py-0.5 mt-2 rounded text-xs font-medium bg-pink-100 text-pink-800">
                          Women in Contemporary Science in Africa
                        </span>
                      )}
                      {submission.isBelispointAcademic && (
                        <span className="inline-flex items-center px-2 py-0.5 mt-2 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          Belispoint Academic
                        </span>
                      )}
                      {submission.isKidnappingForRansom && (
                        <span className="inline-flex items-center px-2 py-0.5 mt-2 rounded text-xs font-medium bg-red-100 text-red-800">
                          Kidnapping For Ransom
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <p className="text-sm text-gray-900">{submission.date}</p>
                    <p className="text-xs text-gray-500">{submission.id}</p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`status-text ${getStatusClass(submission.status)}`}>
                      {submission.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <ul className="text-sm space-y-1">
                      <li className="text-green-600">Accepted: {submission.reviewerInvitations?.accepted || 0}</li>
                      <li className="text-red-600">Declined: {submission.reviewerInvitations?.declined || 0}</li>
                      <li className="text-yellow-600">Pending: {submission.reviewerInvitations?.pending || 0}</li>
                    </ul>
                  </td>
                  <td className="px-6 py-4">
                    <ul className="text-sm space-y-1">
                      <li className="text-green-600">Accepted: {submission.editorInvitations?.accepted || 0}</li>
                      <li className="text-red-600">Declined: {submission.editorInvitations?.declined || 0}</li>
                      <li className="text-yellow-600">Pending: {submission.editorInvitations?.pending || 0}</li>
                    </ul>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleViewSubmission(submission)}
                        className="text-sm font-medium text-[--asfi-purple] hover:text-[--asfi-blue]"
                      >
                        View
                      </button>
                      {/* <button
                        onClick={() => handleDeleteClick(submission.id)}
                        className="text-sm font-medium text-red-500 hover:text-red-700"
                      >
                        Delete
                      </button> */}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <span className="px-3 py-1 text-sm">
                Page {currentPage}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="footer text-center py-4 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          {new Date().getFullYear()} © ASFI - all rights reserved
        </p>
      </footer>

      {/* View Submission Modal - Enhanced with all sections */}
      {showViewModal && selectedSubmission && (
        <SubmissionDetailsModal
          submission={selectedSubmission}
          onClose={() => {
            setShowViewModal(false);
            setSelectedSubmission(null);
          }}
          onArchiveClick={handleDeleteClick}
        />
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Delete Submission</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Are you sure you want to delete this submission?
                  </p>
                </div>
              </div>
              
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Deleted submissions cannot be restored. Authors will be notified about this action.
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={handleDeleteCancel}
                  disabled={deleteLoading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleteLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Confirm Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add custom CSS for status badges */}
      <style jsx>{`
        .status-text {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        .status-blue {
          background-color: #dbeafe;
          color: #1e40af;
        }
        .status-yellow {
          background-color: #fef3c7;
          color: #92400e;
        }
        .status-orange {
          background-color: #ffedd5;
          color: #9a3412;
        }
        .status-green {
          background-color: #d1fae5;
          color: #065f46;
        }
        .status-red {
          background-color: #fee2e2;
          color: #991b1b;
        }
        .status-gray {
          background-color: #f3f4f6;
          color: #1f2937;
        }
      `}</style>
    </div>
  );
};

export default ArchivedSubmissions;